'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { BookOpen, Sparkles, Menu, X } from 'lucide-react'
import { InstrumentedButton } from '@/components/ui/instrumented-button'
import { Button } from '@/components/ui/button'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'
import { useInstrumentedAction } from '@/hooks/useInstrumentedAction'
import { useFontFamily, useFontSize } from '@/contexts/preferences-context'
// Monitoring: AppErrorBoundary wraps ErrorBoundary from '@/components/common/ErrorMonitoringV2'

export default function HomePage() {
  const { fontFamily } = useFontFamily()
  const { fontSize } = useFontSize()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    document.title = 'Infinite Pages - Create Unlimited AI-Powered Stories'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Transform your storytelling with AI-powered continuity tracking, character development, and world-building. Create infinite stories with Claude AI assistance.')
    } else {
      const meta = document.createElement('meta')
      meta.name = 'description'
      meta.content = 'Transform your storytelling with AI-powered continuity tracking, character development, and world-building. Create infinite stories with Claude AI assistance.'
      document.head.appendChild(meta)
    }
  }, [])
  const router = useRouter()

  const instrumentedBrowseAILibrary = useInstrumentedAction(
    () => router.push('/ai-library'),
    { eventName: 'homepage.cta.ai_library', component: 'homepage' }
  );

  return (
    <AppErrorBoundary source="home-page">
      <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url("/victorian-street-scene.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      />

      {/* Dark Overlay for Readability */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25 z-10"
        style={{
          backgroundAttachment: 'fixed'
        }}
      />

      {/* Content */}
      <div className={`relative z-20 min-h-screen flex flex-col font-${fontFamily} font-${fontSize}`}>
        {/* Navigation */}
        <nav className="px-4 sm:px-6 py-4 flex justify-between items-center border-b border-theme-border-primary">
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 accent-text-primary" />
            <h1 className="text-xl sm:text-2xl font-bold text-theme-text-primary">Infinite Pages</h1>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-4">
            <Link href="/billing">
              <Button variant="ghost" className="text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-secondary">
                Pricing
              </Button>
            </Link>
            <Link href="/auth/signin">
              <Button variant="ghost" className="text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-secondary">
                Sign In
              </Button>
            </Link>
            <Link href="/auth/signup">
              <Button className="accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)]">
                Sign Up
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-theme-text-primary hover:bg-theme-bg-secondary rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-theme-border-primary bg-theme-bg-secondary backdrop-blur-glass">
            <div className="px-4 py-3 flex flex-col gap-2">
              <Link href="/billing" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-tertiary">
                  Pricing
                </Button>
              </Link>
              <Link href="/auth/signin" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-tertiary">
                  Sign In
                </Button>
              </Link>
              <Link href="/auth/signup" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)]">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-6xl font-bold text-theme-text-primary leading-tight">
              Write AI Novels &
              <br />
              <span className="text-theme-text-primary">Earn From Readers</span>
            </h1>
            <p className="text-xl md:text-2xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed">
              The only AI that remembers everything - characters, relationships, world rules.
              <br />
              <span className="text-theme-text-primary font-semibold">Build your readership. Discover your earning potential.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link href="/stories/create">
                <Button
                  size="lg"
                  className="accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)] text-lg px-8 py-6 gap-2 shadow-xl shadow-theme-lg"
                >
                  <Sparkles className="w-5 h-5" />
                  Create Your First Story
                </Button>
              </Link>
              <InstrumentedButton
                size="lg"
                eventName="homepage.cta.ai_library"
                component="homepage"
                onClick={instrumentedBrowseAILibrary}
                variant="outline"
                className="border-2 accent-border-primary/50 accent-text-primary hover:bg-theme-bg-elevated hover:border-accent-primary/50 text-lg px-8 py-6 gap-2 backdrop-blur-sm"
              >
                <BookOpen className="w-5 h-5" />
                Read Free Stories
              </InstrumentedButton>
            </div>
            <p className="text-theme-text-muted text-sm pt-2">
              Keep 70% of reader payments - see what&apos;s possible
            </p>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12">
              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-8 border border-theme-border-card hover:accent-border-primary/60 transition-all hover:transform hover:scale-105 shadow-theme-md hover:shadow-theme-lg">
                <div className="text-5xl mb-4">🧬</div>
                <h3 className="text-theme-text-primary font-bold text-xl mb-3">Story Bible System</h3>
                <p className="text-theme-text-secondary text-base leading-relaxed mb-4">
                  Your world&apos;s DNA. Track hundreds of characters, locations, and relationships. AI never forgets who owns what.
                </p>
                <button type="button" className="accent-text-primary hover:accent-text-secondary text-sm font-medium inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer">
                  See how it works →
                </button>
              </div>

              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-8 border border-theme-border-card hover:accent-border-primary/60 transition-all hover:transform hover:scale-105 shadow-theme-md hover:shadow-theme-lg">
                <div className="text-5xl mb-4">🧠</div>
                <h3 className="text-theme-text-primary font-bold text-xl mb-3">Intelligent Context</h3>
                <p className="text-theme-text-secondary text-base leading-relaxed mb-4">
                  Every chapter knows your entire story. Relationships tracked. Rules enforced. Prophecies remembered.
                </p>
                <button type="button" className="accent-text-primary hover:accent-text-secondary text-sm font-medium inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer">
                  Watch demo →
                </button>
              </div>

              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-8 border border-theme-border-card hover:accent-border-primary/60 transition-all hover:transform hover:scale-105 shadow-theme-md hover:shadow-theme-lg">
                <div className="text-5xl mb-4">📈</div>
                <h3 className="text-theme-text-primary font-bold text-xl mb-3">Reader Platform</h3>
                <p className="text-theme-text-secondary text-base leading-relaxed mb-4">
                  Publish instantly. Readers unlock chapters. You earn 70% of revenue. Track your growth with analytics.
                </p>
                <button type="button" className="accent-text-primary hover:accent-text-secondary text-sm font-medium inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer">
                  Explore the platform →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-24 px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-theme-text-primary mb-12">
              Simple Process. <span className="text-theme-text-primary">Powerful Results.</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 accent-bg-primary rounded-full flex items-center justify-center text-[var(--color-text-on-accent)] text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">Build Your Bible</h3>
                <p className="text-theme-text-secondary text-sm">
                  Create characters, world rules, relationships that become your story&apos;s memory
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 accent-bg-primary rounded-full flex items-center justify-center text-[var(--color-text-on-accent)] text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">Generate Outline</h3>
                <p className="text-theme-text-secondary text-sm">
                  15-beat structure ensures perfect pacing and purpose
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 accent-bg-primary rounded-full flex items-center justify-center text-[var(--color-text-on-accent)] text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">Write With Memory</h3>
                <p className="text-theme-text-secondary text-sm">
                  AI remembers everything. Each chapter builds consistently
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 accent-bg-primary rounded-full flex items-center justify-center text-[var(--color-text-on-accent)] text-2xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">Publish & Grow</h3>
                <p className="text-theme-text-secondary text-sm">
                  Launch your novel. Build your audience. Track real reader data
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center text-theme-text-muted text-sm border-t border-theme-border-primary">
          <p>&copy; 2024 Infinite Pages. Powered by Claude AI.</p>
        </footer>
      </div>
      </div>
    </AppErrorBoundary>
  )
}